import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

export type OfflineGradeSyncIntentBannerProps = {
  visible: boolean;
  /** Stack below the brief “back online” toast when both show. */
  showOnlineRestore: boolean;
  onDismiss: () => void;
  onOpenPublish: () => void;
};

export function OfflineGradeSyncIntentBanner({
  visible,
  showOnlineRestore,
  onDismiss,
  onOpenPublish,
}: OfflineGradeSyncIntentBannerProps): ReactElement | null {
  const { t } = useTranslation();
  if (!visible) return null;

  return (
    <div
      className={`fixed z-[101] animate-in slide-in-from-top duration-500 w-[min(100%,24rem)] max-w-[calc(100vw-1rem)] left-1/2 -translate-x-1/2 ${
        showOnlineRestore
          ? 'top-[calc(max(0.75rem,env(safe-area-inset-top,0.5rem))+7rem)]'
          : 'top-[max(0.75rem,env(safe-area-inset-top,0.5rem))]'
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="px-4 py-3 bg-indigo-600/95 backdrop-blur-xl border border-indigo-400/50 rounded-xl shadow-xl flex flex-col gap-2 text-white">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="font-black text-[10px] uppercase tracking-widest block">
              {t('gradeSync.intentBannerTitle')}
            </span>
            <p className="mt-1 text-[10px] font-semibold text-white/90 leading-snug">
              {t('gradeSync.intentBannerBody')}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors shrink-0"
            aria-label={t('gradeSync.intentBannerDismissAria')}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={onOpenPublish}
          className="w-full py-2 rounded-xl bg-white text-indigo-700 text-[11px] font-black uppercase tracking-widest hover:bg-white/90"
        >
          {t('gradeSync.intentBannerOpen')}
        </button>
      </div>
    </div>
  );
}
